import { useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, TrendingUp, MessageSquare } from 'lucide-react';
import type { AssignmentRequestListItem, AssignmentGrantScope, AssignmentRequestStatus } from '../../types';
import { useAssignmentRequestStore } from '../../store/assignmentRequestStore';
import { useAuthStore } from '../../store/authStore';
import { ConfirmDialog } from '../common';

const usd = (cents: number) => `$${Math.round(cents / 100).toLocaleString()}`;

const STATUS_STYLE: Record<AssignmentRequestStatus, { label: string; cls: string }> = {
  PENDING:   { label: 'Pending',   cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  HELD:      { label: 'Held',      cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  APPROVED:  { label: 'Approved',  cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  DENIED:    { label: 'Denied',    cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  CANCELLED: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
};

export function RequestCard({ request, layout = 'full' }: { request: AssignmentRequestListItem; layout?: 'full' | 'compact' }) {
  const { approve, deny, hold, cancel, isSaving } = useAssignmentRequestStore();
  const { user, hasAnyRole } = useAuthStore();
  const isManager = hasAnyRole('MANAGER');
  const isMine = user?.id === request.requestedBy.id;

  const context = request.context;
  const coachName = `${request.person.firstName} ${request.person.lastName}`.trim();
  const [transferOpen, setTransferOpen] = useState(false);
  const [note, setNote] = useState('');

  const run = (fn: () => Promise<void>, ok: string) => async () => {
    try { await fn(); toast.success(ok); } catch (e) { toast.error(e instanceof Error ? e.message : 'Something went wrong'); }
  };

  const style = STATUS_STYLE[request.status];
  const statusBadge = (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0 ${style.cls}`}>{style.label}</span>
  );

  const header = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{coachName}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
          {[request.person.position, request.organization.name, request.organization.state].filter(Boolean).join(' • ')}
        </p>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Requested by <span className="font-medium">{request.requestedBy.displayName ?? 'a rep'}</span>
          {context && context.requesterActivityCount > 0 && <> · {context.requesterActivityCount} activit{context.requesterActivityCount === 1 ? 'y' : 'ies'} logged</>}
        </p>
        {request.note && <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 italic">“{request.note}”</p>}
      </div>
      {statusBadge}
    </div>
  );

  // Rep "My requests" tracking view — the backend omits `context` for a rep's own view.
  if (!context) {
    return (
      <div className={`card ${layout === 'full' ? 'p-4' : 'p-3'} space-y-3`}>
        {header}
        {request.status === 'APPROVED' && (
          <p className="text-sm text-emerald-700 dark:text-emerald-300">Approved — this coach is now assigned to you.</p>
        )}
        {(request.status === 'PENDING' || request.status === 'HELD') && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Under review by your manager.</p>
        )}
        {(request.status === 'DENIED' || request.status === 'HELD') && request.decisionNote && (
          <p className="text-sm text-gray-700 dark:text-gray-300">Manager note: <span className="italic">“{request.decisionNote}”</span></p>
        )}
        {isMine && (request.status === 'PENDING' || request.status === 'HELD') && (
          <button className="btn-secondary" disabled={isSaving} onClick={run(() => cancel(request.id), 'Request cancelled')}>Cancel my request</button>
        )}
      </div>
    );
  }

  // Manager decision view (full live context).
  const { currentOwner, fundraiser } = context;
  const schoolOwner = currentOwner.schoolNamedAccount;
  const doApprove = (scope: AssignmentGrantScope) => run(() => approve(request.id, scope), 'Request approved');
  const onWholeSchool = () => { if (schoolOwner && schoolOwner.userId !== request.requestedBy.id) setTransferOpen(true); else void doApprove('SCHOOL')(); };

  return (
    <div className={`card ${layout === 'full' ? 'p-4' : 'p-3'} space-y-3`}>
      {header}

      {/* ② Current ownership — always rendered */}
      {currentOwner.isOwnedByOther ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="space-y-1">
              {currentOwner.coachDirect && currentOwner.coachDirect.userId !== request.requestedBy.id && (
                <p>This coach is directly assigned to <span className="font-semibold">{currentOwner.coachDirect.displayName ?? 'another rep'}</span>.</p>
              )}
              {schoolOwner && schoolOwner.userId !== request.requestedBy.id && (
                <p>{request.organization.name} is already owned by <span className="font-semibold">{schoolOwner.displayName ?? 'another rep'}</span> (named account). Approving <em>whole school</em> will transfer it.</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
          <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /><span>Unassigned — this coach and school are currently unowned.</span></div>
        </div>
      )}

      {/* ③ Fundraiser context */}
      <div className="text-xs text-gray-600 dark:text-gray-400">
        <div className="flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" />
          {fundraiser.lifetimeCount > 0
            ? <span>{fundraiser.lifetimeCount} lifetime fundraiser{fundraiser.lifetimeCount === 1 ? '' : 's'} · {usd(fundraiser.lifetimeTotalRaisedCents)} raised</span>
            : <span>No fundraiser history on record</span>}
        </div>
        {fundraiser.requesterRanFundraisers && (
          <p className="mt-1 font-medium text-blast-700 dark:text-blast-300">
            ✓ {request.requestedBy.displayName ?? 'This rep'} ran {fundraiser.creditedReps.find((r) => r.salesRepUserId === request.requestedBy.id)?.fundraiserCount ?? 0} fundraiser(s) at this school.
          </p>
        )}
      </div>

      {/* Actions */}
      {isManager && request.status !== 'APPROVED' && request.status !== 'DENIED' && request.status !== 'CANCELLED' && (
        <div className="space-y-2 pt-1">
          {layout === 'full' && (
            <label className="block">
              <span className="sr-only">Note</span>
              <div className="flex items-center gap-1.5 text-xs text-gray-400"><MessageSquare className="h-3 w-3" /> Optional note (deny/hold)</div>
              <input className="input mt-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason…" />
            </label>
          )}
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary flex-1 sm:flex-initial" disabled={isSaving} onClick={doApprove('COACH')}>Approve · this coach</button>
            <button className="btn-secondary flex-1 sm:flex-initial" disabled={isSaving} onClick={onWholeSchool}>Approve · whole school</button>
            <button className="btn-secondary" disabled={isSaving} onClick={run(() => hold(request.id, note || undefined), 'Held for review')}>Hold</button>
            <button className="btn-danger" disabled={isSaving} onClick={run(() => deny(request.id, note || undefined), 'Request denied')}>Deny</button>
          </div>
        </div>
      )}
      {isMine && (request.status === 'PENDING' || request.status === 'HELD') && (
        <button className="btn-secondary" disabled={isSaving} onClick={run(() => cancel(request.id), 'Request cancelled')}>Cancel my request</button>
      )}

      <ConfirmDialog
        open={transferOpen}
        danger
        title="Transfer the whole school?"
        message={`${request.organization.name} is currently owned by ${schoolOwner?.displayName ?? 'another rep'}. Approving "whole school" transfers it (and its coaches) to ${request.requestedBy.displayName ?? 'the requester'}.`}
        confirmLabel="Transfer"
        onConfirm={async () => { setTransferOpen(false); await doApprove('SCHOOL')(); }}
        onCancel={() => setTransferOpen(false)}
      />
    </div>
  );
}
